import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { getLogger } from '@sas/nova-core/logging';
import { Edit } from '@sas/react-svg';
import { renderInlineCssVars, useDesignTokens } from '../../theme';
import { setRef } from '../../util/ref-util';
import { postfixTestId } from '../../util/testId';
import { Avatar } from '../Avatar';
import { useOnAvatarUpdate, usePublishAvatarUpdate } from '../Avatar/__internal__/useOnAvatarUpdate';
import { Icon } from '../Icon';
import { BaseImageSelector } from '../ImageSelector/__internal__/BaseImageSelector';
import { withLabels } from '../withLabels';
import { getAvatarSelectorDataProvider } from './__internal__/AvatarSelectorDataProvider';
import { useAvatarSelectorA11yInfo } from './__internal__/useAvatarSelectorA11yInfo';
import cn from './AvatarSelector.css.json';
import './AvatarSelector.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const logger = getLogger('AvatarSelector');
export const AvatarSelector = ({
  imageSelectorProps,
  dataProvider = getAvatarSelectorDataProvider(),
  ...props
}) => {
  const avatarRef = useRef(null);
  const [isDefaultAvatar, setIsDefaultAvatar] = useState(true);
  const {
    tooltip,
    ariaLabel
  } = useAvatarSelectorA11yInfo({
    ...props,
    dataProvider
  });
  const [fetchedTime, setFetchedTime] = useState();
  const fetchCounter = useRef(0);
  const publishAvatarUpdate = usePublishAvatarUpdate(dataProvider, props.userId, props.type);
  useEffect(() => {
    return () => {
      // Increment the fetchCounter to discard the fetch response after unmounting
      fetchCounter.current += 1;
    };
  }, []);
  const updateIsDefaultAvatar = useCallback(() => {
    const fetchCount = ++fetchCounter.current;
    dataProvider.getAvatar?.(props.userId, props.type).then(src => {
      if (fetchCounter.current > fetchCount) return;
      setIsDefaultAvatar(src.length === 0);
    }).catch(() => {
      if (fetchCounter.current > fetchCount) return;
      setIsDefaultAvatar(true);
      logger.warn(
      // eslint-disable-next-line @sas/i18n/jsx-to-localize
      `Failed to fetch the avatar details info for id:${props.userId} and type: ${props.type}. Using default avatar.`);
    }).finally(() => {
      if (fetchCounter.current > fetchCount) return;
      setFetchedTime(Date.now());
    });
  }, [dataProvider, props.userId, props.type]);
  useOnAvatarUpdate(dataProvider, updateIsDefaultAvatar, props.userId, props.type, fetchedTime);
  useEffect(updateIsDefaultAvatar, [updateIsDefaultAvatar]);
  const onRemoveAvatar = () => {
    return new Promise((resolve, reject) => {
      dataProvider.deleteAvatar(props.userId, props.type).then(() => {
        publishAvatarUpdate();
        resolve();
      }).catch(reject);
    });
  };
  const onSaveAvatar = src => {
    return new Promise((resolve, reject) => {
      dataProvider.saveAvatar(src, props.userId, props.type).then(() => {
        props.onSaveComplete?.({
          status: 'success'
        });
        publishAvatarUpdate();
        resolve();
      }).catch(() => {
        props.onSaveComplete?.({
          status: 'failure'
        });
        reject();
      });
    });
  };
  const dt = useDesignTokens();
  const tokens = renderInlineCssVars({
    bg: dt.tokens['color-action-background-emphasis-normal'],
    'edit-icon-bd': dt.tokens['color-action-border-emphasis-normal'],
    'edit-icon-bg': dt.tokens['color-action-icon-control-normal'],
    'edit-icon-bw': dt.tokens['size-border-width-s']
  }, 'avatar-selector');
  return /*#__PURE__*/_jsx(Fragment, {
    children: /*#__PURE__*/_jsx(BaseImageSelector, {
      ellipse: true
      // Set the src to 'dummy string' when avatar is not the default one to enable the "Remove image" menu item.
      // Since it won't be shown to the end-user so no need to localize
      ,
      src: isDefaultAvatar ? undefined : 'dummy string',
      imageSize: {},
      renderImageContainer: ({
        ref,
        onPress,
        onFocus
      }) => /*#__PURE__*/_jsxs("div", {
        style: tokens,
        className: cn.avatarContainer,
        ref: ref,
        onFocus: () => {
          if (avatarRef.current) {
            onFocus(avatarRef.current);
          }
        },
        children: [/*#__PURE__*/_jsx(Avatar, {
          ...props,
          dataProvider: dataProvider,
          ref: element => {
            avatarRef.current = element;
            setRef(props.avatarRef, element);
          },
          useAsButton: true,
          onPress: onPress,
          className: cn.avatar,
          size: props.size ?? 'extraLarge',
          tooltip: tooltip,
          ariaLabel: ariaLabel,
          testId: postfixTestId(props.testId, 'avatar')
        }), /*#__PURE__*/_jsx("div", {
          className: cn.editIcon,
          onClick: onPress,
          children: /*#__PURE__*/_jsx(Icon, {
            icon: Edit,
            theme: dt => {
              return {
                '--icon-default-override-fg': dt.tokens['color-text-neutral-inverse']
              };
            },
            tooltip: tooltip
          })
        })]
      }),
      onSaveImage: onSaveAvatar,
      onRemoveImage: onRemoveAvatar,
      testId: postfixTestId(props.testId, 'imageSelector'),
      ...imageSelectorProps
    })
  });
};
const WithLabels = withLabels(AvatarSelector);
export const AvatarSelectorWithLabels = props => {
  const dataProvider = props.dataProvider ?? getAvatarSelectorDataProvider();
  // withLabels can only concatenate the label with customized the ariaLabel, so feed the default ariaLabel as
  // customized one to make sure the default ariaLabel is also concatenated
  const {
    ariaLabel
  } = useAvatarSelectorA11yInfo({
    ...props,
    dataProvider
  });
  return /*#__PURE__*/_jsx(WithLabels, {
    ...props,
    dataProvider: dataProvider,
    ariaLabel: ariaLabel
  });
};
//# sourceMappingURL=AvatarSelector.js.map